import { useQuery } from '@tanstack/react-query';
import { channelWa } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Text, Button, SimpleGrid } from '@repletoryreach/sdk-ui';
import { Alert, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { CopyableId } from '../../../components/CopyableId';
import { TenantName } from '../../../components/TenantName';
import { useT } from '../../../i18n/I18nProvider';

const MUTED = 'var(--rr-color-text-muted)';

/*
 * Readiness per WhatsApp Business Account, asked of Meta.
 *
 * Everything else on this page is a record of what customers did. This is the
 * one part that asks whether the thing works right now, and it exists because
 * of an evening where the Test button was green, the account read perfectly,
 * templates listed, and every single write was refused: the system user had no
 * assignment on the account, and Meta says that only in a sentence that names
 * nothing anybody can act on.
 *
 * Three questions, per account, in the order they break:
 *   is a user assigned with rights that can change something,
 *   is the app subscribed so anything inbound ever arrives,
 *   and is each number free to run on the Cloud API.
 *
 * A grey badge is not a green one. Meta declines to answer some of these
 * depending on the token, and "we could not tell" has to stay visibly apart
 * from "yes", because collapsing the two is the whole failure being fixed.
 */
export function WbaReadiness() {
  const t = useT();

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['admin', 'waba-readiness'],
    queryFn: () => channelWa.wabaReadiness({ limit: 8 }),
    // Live Graph reads, three per account. Worth a minute of staleness rather
    // than a fresh round trip every time somebody switches tabs.
    staleTime: 60_000,
    refetchOnWindowFocus: false
  });

  const rows = data?.data ?? [];

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="center" wrap="wrap">
        <Stack gap={2}>
          <Text role="bodySmall" fw={600}>{t('gateways.wba.readiness.title')}</Text>
          <Text role="caption" c={MUTED}>{t('gateways.wba.readiness.description')}</Text>
        </Stack>
        <Button size="xs" variant="outline" loading={isFetching} onClick={() => void refetch()}>
          {t('action.refresh')}
        </Button>
      </Group>

      {error && (
        <Alert color="error" title={t('gateways.wba.readiness.loadError')}>
          <Text role="bodySmall">{(error as Error).message}</Text>
        </Alert>
      )}

      {!error && !isLoading && rows.length === 0 && (
        <Text role="caption" c={MUTED}>{t('gateways.wba.readiness.empty')}</Text>
      )}

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        {rows.map((r) => <AccountCard key={`${r.organisation_id}:${r.waba_id}`} row={r} />)}
      </SimpleGrid>
    </Stack>
  );
}

function AccountCard({ row }: { row: channelWa.WabaReadiness }) {
  const t = useT();
  const broken = row.system_user_assigned === false
    || row.app_subscribed === false
    || row.numbers.some((n) => !n.usable);

  return (
    <Card withBorder>
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
          <Stack gap={2} style={{ minWidth: 0 }}>
            <TenantName id={row.organisation_id} />
            <CopyableId value={row.waba_id} label={t('gateways.numbers.copyWaba')} chars={14} />
          </Stack>
          <StatusBadge status={broken ? 'down' : 'healthy'}>
            {broken ? t('gateways.wba.readiness.needsWork') : t('gateways.wba.readiness.working')}
          </StatusBadge>
        </Group>

        <Flag
          label={t('gateways.wba.readiness.assigned')}
          value={row.system_user_assigned}
          detail={row.assigned_detail}
        />
        <Flag
          label={t('gateways.wba.readiness.subscribed')}
          value={row.app_subscribed}
          detail={row.subscribed_detail}
        />

        <Stack gap={4}>
          <Text role="caption" c={MUTED}>{t('gateways.wba.readiness.numbers')}</Text>
          {row.numbers.length === 0 ? (
            <Text role="caption" c={MUTED}>
              {row.numbers_detail ?? t('gateways.wba.readiness.noNumbers')}
            </Text>
          ) : (
            row.numbers.map((n) => (
              <Group key={n.id} align="flex-start" wrap="nowrap" gap="sm">
                <StatusBadge status={n.usable ? (n.state === 'limited' ? 'warning' : 'healthy') : 'down'}>
                  {t(`gateways.wba.readiness.numberState.${n.state}`, n.state)}
                </StatusBadge>
                {/* Meta's own field names help nobody, so the service hands
                    over the sentence that names what has to be done. */}
                <Text role="caption" c={MUTED} style={{ minWidth: 0 }}>{n.detail}</Text>
              </Group>
            ))
          )}
        </Stack>
      </Stack>
    </Card>
  );
}

/**
 * A three-state answer. Undefined is not false: Meta will not answer some of
 * these depending on the token, and a grey "not known" tells an operator to go
 * and look, where a red one would send them to fix something that may be fine.
 */
function Flag({ label, value, detail }: { label: string; value?: boolean; detail: string }) {
  const t = useT();
  const status = value === true ? 'healthy' : value === false ? 'down' : 'pending';
  const word = value === true
    ? t('gateways.wba.readiness.yes')
    : value === false
      ? t('gateways.wba.readiness.no')
      : t('gateways.wba.readiness.unknown');

  return (
    <Stack gap={2}>
      <Group gap="sm" align="center" wrap="nowrap">
        <StatusBadge status={status}>{word}</StatusBadge>
        <Text role="bodySmall">{label}</Text>
      </Group>
      <Text role="caption" c={MUTED}>{detail}</Text>
    </Stack>
  );
}
